<template>
  <div v-if="isCreating" class="h-full bg-gray flex-1 flex flex-center">
    <vue-loading type="spiningDubbles" color="#efad4c" :size="{ width: '100px', height: '100px' }" />
  </div>

  <div v-else class="h-full flex-1 bg-gray scroll-y flex flex-column align-items">
    <h2 class="mx-1">{{ $t('novelCreatePage.title') }}</h2>
    <div class="card flex flex-column">
      <!-- Section 1: Title -->
      <div class="px-1 section">
        <h3>{{ $t('novelCreatePage.titleInput.label') }}</h3>
        <div class="field-header">
          <span class="field-label">{{ $t('novelCreatePage.titleInput.fieldLabel') }}</span>
          <span class="char-counter">{{ title.length }}/80</span>
        </div>
        <input
          class="field-input"
          type="text"
          v-model="title"
          :placeholder="$t('novelCreatePage.titleInput.placeholder')"
          autofocus
          maxlength="80"
        />
        <span class="field-hint">{{ $t('novelCreatePage.titleInput.description') }}</span>
      </div>
      <!-- Section 2: Category/Genre -->
      <div class="px-1 section">
        <h3>{{ $t('novelCreatePage.genreCategory.label') }}</h3>
        <span class="field-label">{{ $t('novelCreatePage.genreCategory.categoryLabel') }}</span>
        <select
          class="field-select"
          :class="{ 'placeholder-selected': !genre }"
          :value="genre ? (typeof genre === 'string' ? genre : genre.id) : ''"
          @change="onChangeGenre"
        >
          <option value="">{{ $t('novelCreatePage.genreCategory.genrePlaceholder') }}</option>
          <option v-for="item in translatedGenreList" :key="item.id" :value="item.id">
            {{ item.name }}
          </option>
        </select>
        <span class="field-label mt-section">{{ $t('novelCreatePage.genreCategory.genreLabel') }}</span>
        <div class="genre-chips">
          <div
            v-for="item in translatedCategoryList"
            :key="item.id"
            class="genre-chip"
            :class="{
              selected: isCategorySelected(item),
              disabled: !isCategorySelected(item) && categories !== null && categories.length >= maxCategoryCount
            }"
            @click="toggleCategory(item)"
          >
            {{ item.name }}
          </div>
        </div>
      </div>
      <!-- Section 3: Word Count -->
      <div class="px-1 section">
        <h3>{{ $t('novelCreatePage.wordLength.label') }}</h3>
        <span class="field-label">{{ $t('novelCreatePage.wordLength.fieldLabel') }}</span>
        <div class="word-length-row">
          <input
            class="field-input"
            type="text"
            v-model="wordLength"
            :placeholder="$t('novelCreatePage.wordLength.placeholder')"
            maxlength="18"
          />
          <span class="bold">{{ $t('novelCreatePage.wordLength.suffix') }}</span>
        </div>
        <p class="field-hint field-hint--sm" v-html="$t('novelCreatePage.wordLength.description')"></p>
        <p class="reference-label">{{ $t('novelCreatePage.wordLength.referenceLabel') }}</p>
        <p class="field-hint field-hint--sm field-hint--normal">{{ $t('novelCreatePage.wordLength.reference') }}</p>
      </div>
      <div class="section-divider"></div>
      <!-- Script Mode Toggle -->
      <div class="px-1 py-1\/2 script-mode-section">
        <label class="toggle-row">
          <span class="toggle-switch">
            <input type="checkbox" v-model="isScriptMode" />
            <span class="toggle-slider"></span>
          </span>
          <span class="toggle-label">{{ $t('novelCreatePage.scriptMode.label') }}</span>
        </label>
        <div class="toggle-description">
          <p class="toggle-description-text">{{ $t('novelCreatePage.scriptMode.description') }}</p>
          <button class="toggle-link" @click="showScriptModeHelp">
            {{ $t('novelCreatePage.scriptMode.learnMore') }}
          </button>
        </div>
      </div>
    </div>
    <div class="submit-area">
      <button class="submit-btn" type="submit" :disabled="!canSubmit" @click.prevent="onCreateButtonClick">
        {{ $t('novelCreatePage.submitButton') }}
      </button>
      <button class="skip-btn" type="button" @click="onSkipStart">
        {{ $t('novelCreatePage.skipButton') }}
      </button>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import { VueLoading } from "vue-loading-template";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import ScriptModeHelpDialog from "@/components/ui/dialogs/ScriptModeHelpDialog.vue";
import { genre as genreList, category as categoryList } from "@/lib/novelSettings";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
  MAX_CATEGORY_COUNT,
} from "@/lib/utils/novelSettingsHelper";
import { Novel, NovelSetting } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";

interface Data {
  title: string;
  genre: NovelSetting | string | null;
  categories: NovelSetting[] | string[] | null;
  wordLength: number | null;
  genreList: NovelSetting[];
  categoryList: NovelSetting[];
  isCreating: boolean;
  isScriptMode: boolean;
  unwatchAppTheme: (() => void) | null;
  unwatchEffectiveTheme: (() => void) | null;
  nolaAnalytics: NolaAnalytics;
}

interface Methods {
  onCreateButtonClick: () => Promise<void>;
  onChangeGenre: (event: Event) => void;
  addCategory: (item: NovelSetting) => void;
  removeCategory: (item: NovelSetting | string) => void;
  getCategoryName: (item: NovelSetting | string) => string;
  isCategorySelected: (item: NovelSetting) => boolean;
  toggleCategory: (item: NovelSetting) => void;
  updateAppBackgroundColor: () => void;
  createNovel: (isSkipStart?: boolean) => Promise<void>;
  createManuscript: (novelId: String) => void;
  onSkipStart: () => Promise<void>;
  showScriptModeHelp: () => void;
}

interface Computed {
  isValidWordLength: boolean;
  canSubmit: boolean;
  translatedGenreList: NovelSetting[];
  translatedCategoryList: NovelSetting[];
  maxCategoryCount: number;
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    VueLoading,
  },

  mounted() {
    this.updateAppBackgroundColor();
    this.unwatchAppTheme = this.$store.watch(
      (state) => state.generalModule.appTheme,
      () => {
        this.updateAppBackgroundColor();
      }
    );
    this.unwatchEffectiveTheme = this.$store.watch(
      (state, getters) => getters["generalModule/effectiveTheme"],
      () => {
        this.updateAppBackgroundColor();
      }
    );
  },
  beforeDestroy() {
    document.getElementById("app")?.style.removeProperty("background-color");
    if (this.unwatchAppTheme) {
      this.unwatchAppTheme();
    }
    if (this.unwatchEffectiveTheme) {
      this.unwatchEffectiveTheme();
    }
  },

  data() {
    return {
      title: "",
      genre: null,
      categories: null,
      wordLength: null,
      genreList,
      categoryList,
      isCreating: false,
      isScriptMode: false,
      unwatchAppTheme: null,
      unwatchEffectiveTheme: null,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
    };
  },

  methods: {
    updateAppBackgroundColor() {
      const appElement = document.getElementById("app");
      if (appElement) {
        const computedStyle = getComputedStyle(document.documentElement);
        const bgColor = computedStyle.getPropertyValue("--color-background-normal").trim();
        appElement.style.setProperty("background-color", bgColor);
      }
    },
    async onCreateButtonClick() {
      try {
        this.isCreating = true;
        await this.createNovel();
      } catch (e) {
        console.log(e);
      } finally {
        this.isCreating = false;
      }
    },
    onChangeGenre(event: Event) {
      const target = event.target as HTMLSelectElement;
      const { value } = target;
      if (value === "") {
        this.genre = null;
      } else {
        const item = this.translatedGenreList.find((g) => g.id === value);
        if (item) this.genre = item;
      }
    },
    addCategory(item: NovelSetting) {
      // 内部名categoryの選択上限(画面表示は「ジャンル」)。最大MAX_CATEGORY_COUNT個まで制限
      if (this.categories && this.categories.length >= MAX_CATEGORY_COUNT) {
        return;
      }
      // 内部名categoryを追加(画面表示は「ジャンル」)
      if (!this.categories) {
        this.categories = [item];
      } else {
        const itemId = typeof item === 'string' ? item : item.id;
        if (this.categories.some((c) => (typeof c === 'string' ? c : c.id) === itemId)) {
          return;
        }
        this.categories = [...this.categories, item];
      }
    },
    removeCategory(item: NovelSetting | string) {
      // 内部名categoryを削除(画面表示は「ジャンル」)
      if (!this.categories) {
        return;
      }
      const itemId = typeof item === 'string' ? item : item.id;
      this.categories = this.categories.filter((c) => (typeof c === 'string' ? c : c.id) !== itemId);
      if (this.categories.length === 0) {
        this.categories = null;
      }
    },
    getCategoryName(item: NovelSetting | string): string {
      // 内部名categoryの名前を取得(画面表示は「ジャンル」。stringの場合は翻訳済みリストから取得)
      if (typeof item === 'string') {
        const category = this.translatedCategoryList.find((c) => c.id === item);
        return category ? category.name : item;
      }
      return item.name;
    },
    isCategorySelected(item: NovelSetting): boolean {
      if (!this.categories) return false;
      const itemId = item.id;
      return this.categories.some((c) => (typeof c === 'string' ? c : c.id) === itemId);
    },
    toggleCategory(item: NovelSetting): void {
      if (this.isCategorySelected(item)) {
        this.removeCategory(item);
      } else {
        this.addCategory(item);
      }
    },

    async createNovel(isSkipStart?: boolean) {
      const { isValidWordLength } = this;

      let { title, wordLength } = this;
      if (isSkipStart) {
        title = this.$t('novelCreatePage.defaultTitle');
        wordLength = null;
      } else if (!title) {
        return;
      }

      if (!isValidWordLength) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: this.$t('novelCreatePage.inputErrorTitle'),
          content: this.$t('novelCreatePage.inputErrorMessage'),
          action: this.$t('common.close'),
        };
        completeDialog.show(data);
        return;
      }

      let { genre, categories } = this;

      if (isSkipStart) {
        genre = null;
        categories = null;
      } else {
        if (genre && typeof genre !== "string") {
          genre = genre.id;
        }

        // 内部名categoryがNovelSetting[]の場合はIDの配列に変換(画面表示は「ジャンル」)
        if (categories && categories.length > 0 && typeof categories[0] !== "string") {
          categories = (categories as NovelSetting[]).map((category: NovelSetting) => category.id);
        }
      }

      const { dispatch } = this.$store;
      const { novelId } = (await dispatch("novelModule/createNovel", {
        title,
        genre,
        category: categories,
        wordLength,
        isSkip: isSkipStart,
        manuscriptMode: this.isScriptMode ? "script" : "default",
      })) as Novel;

      if (this.isScriptMode) {
        try {
          this.nolaAnalytics.logButtonEvent(NolaItemId.ScriptModeNovelCreated, novelId);
        } catch {
          // analytics エラーは作品作成後の処理に影響させない
        }
      }

      if (wordLength) {
        const detail = [
          {
            name: "word-length",
            value: wordLength,
          },
        ];
        dispatch("novelThemeModule/updateTheme", { novelId, detail });
      }

      this.createManuscript(novelId);

      this.$ga.event("novel", "create-novel", "執筆をはじめる", 0);
      this.$router.push({ name: "theme", params: { novelId } });
    },
    createManuscript(novelId) {
      this.$store.dispatch("manuscriptModule/createManuscript", {
        novelId,
        title: "",
        content: "",
        manuscriptFolderId: null,
      });
    },
    async onSkipStart() {
      try {
        this.isCreating = true;
        await this.createNovel(true);
      } catch (e) {
        console.log(e);
      } finally {
        this.isCreating = false;
      }
    },
    showScriptModeHelp() {
      new Dialog(ScriptModeHelpDialog).show();
    },
  },

  computed: {
    maxCategoryCount() {
      return MAX_CATEGORY_COUNT;
    },
    isValidWordLength() {
      const { wordLength } = this;
      const isNumber = (value: any) => !Number.isNaN(Number(value));
      if (wordLength && !isNumber(wordLength)) {
        return false;
      }
      return true;
    },
    canSubmit() {
      return this.title && this.genre !== null && this.categories !== null;
    },
    translatedGenreList() {
      return getTranslatedGenreList(this.$t);
    },
    translatedCategoryList() {
      return getTranslatedCategoryList(this.$t);
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

// container
.card {
  background-color: var(--color-surface-base);
  border-radius: var(--radius-md);
  padding-top: 1rem;
  padding-bottom: 1rem;
  width: 700px;
  max-width: 90%;
  box-shadow: var(--shadow-md);

  h2,
  h3 {
    color: var(--color-text-normal);
  }
}

.section {
  display: flex;
  flex-direction: column;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;

  h3 {
    margin: 0 0 12px;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-normal);
  }
}

// field sub-label + counter row
.field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.field-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
  display: block;
  margin-bottom: 4px;
}

.mt-section {
  margin-top: 16px;
}

.char-counter {
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
}

// inputs
.field-input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 8px 12px;
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  border: none;
  border-radius: var(--radius-xs);
  font-size: var(--font-size-md);

  &::placeholder {
    color: var(--color-text-low);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.field-hint {
  margin: 6px 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
  line-height: 1.6;

  &--sm {
    font-size: var(--font-size-sm);
  }

  &--normal {
    color: var(--color-text-normal);
  }
}

// category dropdown
.field-select {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  margin-top: 4px;
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-xs);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath fill='%23808080' d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding-right: 40px;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &.placeholder-selected {
    color: var(--color-text-normal);
  }

  option {
    background-color: var(--color-background-normal);
    color: var(--color-text-normal);
  }
}

// genre chips (unified toggle list)
.genre-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.genre-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  border: none;
  cursor: pointer;
  font-size: var(--font-size-sm);
  user-select: none;

  @include hover-overlay;

  &.selected {
    background-color: var(--color-background-invert);
    color: var(--color-text-invert);
    font-weight: var(--font-weight-bold);

    &::after {
      display: none;
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &::after {
      display: none;
    }
  }
}

// word count row
.word-length-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  align-self: flex-start;

  .field-input {
    width: 240px;
    flex: none;
  }
}

.bold {
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  color: var(--color-text-normal);
}

.reference-label {
  margin: 12px 0 4px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

// layout helpers
.h-full {
  height: 100%;
}

.w-full {
  width: 100%;
}

.flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

.flex-center {
  align-items: center;
  justify-content: center;
}

.align-items {
  align-items: center;
}

// padding / margin
.px-1 {
  padding-right: 1.5rem;
  padding-left: 1.5rem;
}

.py-1\/2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.px-4 {
  padding-right: 4rem;
  padding-left: 4rem;
}

.my-1 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.my-1\/2 {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

h2.mx-1 {
  margin-top: 2.5rem;
  font-size: var(--font-size-lg);
  color: var(--color-text-normal);
}

// border
// color
.bg-gray {
  background-color: var(--color-background-normal);
}

.red {
  color: var(--color-warning);
}

.scroll-y {
  overflow-y: scroll;
}

// submit button
.submit-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: 1.5rem 0;
  width: 700px;
  max-width: 90%;
}

.submit-btn {
  white-space: nowrap;
  padding: 10px 32px;
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  &:disabled {
    background-color: var(--color-surface-muted);
    color: var(--color-inactive);
    border: 1px solid var(--color-border);
    cursor: not-allowed;
  }
}

.skip-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
  text-decoration: underline;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    opacity: 0.7;
  }
}

// script mode toggle
.section-divider {
  margin: 8px 1.5rem 20px;
  border-top: 1px solid var(--color-border);
}

.script-mode-section {
  width: 100%;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 56px;
  height: 32px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    inset: 0;
    background-color: var(--color-inactive);
    border-radius: 32px;
    transition: background-color 0.25s;

    &::before {
      content: "";
      position: absolute;
      width: 26px;
      height: 26px;
      left: 3px;
      bottom: 3px;
      background-color: #fff;
      border-radius: 50%;
      transition: transform 0.25s;
    }
  }

  input:checked + .toggle-slider {
    background-color: var(--color-primary);
  }

  input:checked + .toggle-slider::before {
    transform: translateX(24px);
  }
}

.toggle-label {
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}

.toggle-description {
  margin: 6px 0 0 0;
  font-size: var(--font-size-xs);
  line-height: 1.6;
}

.toggle-description-text {
  margin: 0;
  color: var(--color-text-normal);
}

.toggle-link {
  display: block;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  color: var(--color-primary);
  text-decoration: underline;
  cursor: pointer;
  text-align: left;

  &:hover {
    opacity: 0.8;
  }
}
</style>
